import { Auth } from 'aws-amplify';
import { ROUTE } from '../pages/routes';

/**
 * Centralized session expiry handler for consistent error handling across the application
 */
export class SessionExpiryHandler {
  private static instance: SessionExpiryHandler;
  private isRedirecting = false;

  private constructor() {}

  public static getInstance(): SessionExpiryHandler {
    if (!SessionExpiryHandler.instance) {
      SessionExpiryHandler.instance = new SessionExpiryHandler();
    }
    return SessionExpiryHandler.instance;
  }

  /**
   * Handle session expiry by cleaning up auth state and redirecting to login
   */
  public async handleSessionExpiry(): Promise<void> {
    if (this.isRedirecting) {
      return; // Prevent multiple redirects
    }

    this.isRedirecting = true;

    try {
      console.log('[SessionExpiryHandler] Session expired, cleaning up and redirecting to login');
      
      // Clear any cached authentication data
      await this.clearAuthState();
      
      // Redirect to login page
      window.location.replace(ROUTE.LOGIN);
    } catch (error) {
      console.error('[SessionExpiryHandler] Error during session expiry handling:', error);
      // Force redirect even if cleanup fails
      window.location.replace(ROUTE.LOGIN);
    }
  }

  /**
   * Check if an error indicates session expiry (401 Unauthorized)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public isSessionExpiredError(error: any): boolean {
    return (
      error?.networkError?.statusCode === 401 ||
      error?.statusCode === 401 ||
      error?.code === 'UnauthorizedException' ||
      error?.message?.includes('401') ||
      error?.message?.includes('Unauthorized')
    );
  }

  /**
   * Handle API errors and redirect if session expired
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async handleApiError(error: any): Promise<void> {
    if (this.isSessionExpiredError(error)) {
      await this.handleSessionExpiry();
    }
  }

  /**
   * Clear authentication state and cached data
   */
  private async clearAuthState(): Promise<void> {
    try {
      // Sign out from AWS Amplify to clear tokens
      await Auth.signOut({ global: true });
    } catch (error) {
      console.warn('[SessionExpiryHandler] Error during sign out:', error);
      // Continue with redirect even if sign out fails
    }
  }

  /**
   * Reset the redirecting flag (useful for testing)
   */
  public resetRedirectingFlag(): void {
    this.isRedirecting = false;
  }
}

/**
 * Convenience function to get the session expiry handler instance
 */
export const getSessionExpiryHandler = (): SessionExpiryHandler => {
  return SessionExpiryHandler.getInstance();
};

/**
 * Convenience function to handle API errors with session expiry check
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleApiError = async (error: any): Promise<void> => {
  const handler = getSessionExpiryHandler();
  await handler.handleApiError(error);
};

/**
 * Convenience function to check if an error indicates session expiry
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isSessionExpiredError = (error: any): boolean => {
  const handler = getSessionExpiryHandler();
  return handler.isSessionExpiredError(error);
};

/**
 * Utility function to wrap API calls with automatic session expiry handling
 * This can be used to replace try-catch blocks in API functions
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withSessionExpiryHandling = async <T>(
  apiCall: () => Promise<T>,
  errorContext?: string
): Promise<T> => {
  try {
    return await apiCall();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(`[SessionExpiryHandler] API Error${errorContext ? ` in ${errorContext}` : ''}:`, error);
    
    const sessionHandler = getSessionExpiryHandler();
    if (sessionHandler.isSessionExpiredError(error)) {
      await sessionHandler.handleSessionExpiry();
    }
    
    throw error;
  }
};

/**
 * Utility function to handle API errors with consistent logging and session expiry check
 * This replaces the scattered error handling patterns in API files
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleApiErrorWithSessionCheck = async (error: any, context?: string): Promise<void> => {
  const sessionHandler = getSessionExpiryHandler();
  
  if (context) {
    console.error(`[SessionExpiryHandler] API Error in ${context}:`, error);
  } else {
    console.error('[SessionExpiryHandler] API Error:', error);
  }
  
  if (sessionHandler.isSessionExpiredError(error)) {
    await sessionHandler.handleSessionExpiry();
  }
};
